import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Calculator, Upload, Save, Loader2, ChevronDown, ChevronUp, FileDown,
} from "lucide-react";
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from "recharts";

/**
 * Cost Summary (EPC) — Per critical_fixes_frameworks_costsummary §2:
 *  - Cash Inflow has been REMOVED. Final Bid Price IS the bid value.
 *  - Graph changed to cost-composition (pie + per-category bar) instead of income-vs-expense.
 *  - Downloadable template offered (§3.7).
 */

const CURRENCIES = ["INR", "USD", "EUR", "GBP", "AED"];
const PIE_COLORS = ["#002FA7", "#0046DB", "#1E5BFF", "#4F7BFF", "#8AAAFF", "#B7CBFF", "#DDE5FF"];

function fmt(n, currency = "INR") {
  if (n == null) return "—";
  const v = Number(n);
  if (Number.isNaN(v)) return "—";
  return `${currency} ${v.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

export default function CostSummaryModule({ tenderId }) {
  const [schema, setSchema] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currency, setCurrency] = useState("INR");
  const [valueDrafts, setValueDrafts] = useState({});
  const [saving, setSaving] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const [uploadingXlsx, setUploadingXlsx] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: sch }, { data: cur }] = await Promise.all([
          api.get("/cost-summary/schema"),
          api.get(`/cost-summary/${tenderId}`),
        ]);
        setSchema(sch);
        if (cur.summary) {
          setSummary(cur.summary);
          setValueDrafts(cur.summary.values || {});
          setCurrency(cur.summary.currency || "INR");
        }
      } catch (ex) {
        toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
      } finally { setLoading(false); }
    })();
  }, [tenderId]);

  const liveBreakdown = useMemo(() => {
    if (!schema) return [];
    return schema.categories.map((c) => {
      const subtotal = c.items.reduce((acc, it) => acc + (parseFloat(valueDrafts[it.code]) || 0), 0);
      return { ...c, subtotal };
    });
  }, [schema, valueDrafts]);

  const liveFinalBid = useMemo(() => liveBreakdown.reduce((a, c) => a + c.subtotal, 0), [liveBreakdown]);

  const saveValues = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(
        `/cost-summary/${tenderId}/values`,
        { values: valueDrafts, currency },
      );
      setSummary(data);
      toast.success("Cost summary saved");
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    } finally { setSaving(false); }
  };

  const onXlsxUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingXlsx(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/cost-summary/${tenderId}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSummary(data.summary);
      setValueDrafts(data.summary.values || {});
      toast.success(`Parsed ${data.parsed_items} line items from ${file.name}`);
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    } finally { setUploadingXlsx(false); }
  };

  const downloadTemplate = async () => {
    try {
      const resp = await api.get(`/cost-summary/template`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "cost_summary_template.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    }
  };

  const updateValue = (code, raw) => {
    const cleaned = raw.replace(/[^0-9.]/g, "");
    setValueDrafts((s) => ({ ...s, [code]: cleaned }));
  };

  if (loading) {
    return <div className="text-sm text-ink-muted uppercase tracking-wider" data-testid="cost-loading">Loading Cost Summary…</div>;
  }
  if (!schema) {
    return <div className="text-sm text-ink-muted">Schema unavailable.</div>;
  }

  return (
    <div className="space-y-6 animate-fade-up" data-testid="cost-builder">
      {/* Header strip */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="card-flat p-4 md:col-span-2">
          <div className="flex items-center gap-2.5">
            <Calculator className="w-5 h-5 text-brand" />
            <div className="stat-label">Final Bid Price</div>
          </div>
          <div className="font-num text-3xl font-bold mt-2 text-brand" data-testid="cost-final-bid-price">{fmt(liveFinalBid, currency)}</div>
          <div className="text-[11px] text-ink-muted mt-2 uppercase tracking-wider">Sum of category subtotals · this is the bid value</div>
        </div>
        <div className="card-flat p-4 flex flex-col justify-between">
          <div>
            <div className="stat-label">Currency · Actions</div>
            <div className="mt-2 flex items-center gap-2">
              <select value={currency} onChange={(e) => setCurrency(e.target.value)}
                data-testid="cost-currency-select"
                className="px-2 py-1.5 border border-ink-line bg-white rounded-sm text-xs font-num focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand">
                {CURRENCIES.map((c) => <option key={c}>{c}</option>)}
              </select>
              <button onClick={downloadTemplate} data-testid="cost-template-btn"
                className="text-[11px] text-brand hover:underline inline-flex items-center gap-1">
                <FileDown className="w-3 h-3" /> Template
              </button>
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <label className="flex-1 cursor-pointer px-3 py-1.5 text-xs border border-ink-line hover:bg-secondary rounded-sm text-center transition-colors flex items-center justify-center gap-1.5" data-testid="cost-upload-label">
              {uploadingXlsx ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} Upload XLSX
              <input type="file" accept=".xlsx,.xls" onChange={onXlsxUpload} className="hidden" data-testid="cost-upload-input" />
            </label>
            <button onClick={saveValues} disabled={saving} data-testid="cost-save"
              className="flex-1 px-3 py-1.5 text-xs bg-brand text-white hover:bg-brand-hover disabled:opacity-50 rounded-sm transition-colors flex items-center justify-center gap-1.5">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
            </button>
          </div>
        </div>
      </div>

      {/* Categories — entry form */}
      <div className="space-y-4">
        {schema.categories.map((cat) => {
          const isCollapsed = !!collapsed[cat.code];
          const subtotal = liveBreakdown.find((c) => c.code === cat.code)?.subtotal || 0;
          const sharePct = liveFinalBid > 0 ? (subtotal / liveFinalBid) * 100 : 0;
          return (
            <div key={cat.code} className="card-flat" data-testid={`cost-cat-${cat.code}`}>
              <button onClick={() => setCollapsed((s) => ({ ...s, [cat.code]: !isCollapsed }))}
                className="w-full px-5 py-3 flex items-center justify-between border-b border-ink-line hover:bg-secondary/40 transition-colors text-left"
                data-testid={`cost-cat-toggle-${cat.code}`}>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.2em] text-ink-muted">Category {cat.id}</div>
                  <div className="font-display font-bold text-base tracking-tight mt-0.5">{cat.label}</div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="text-[11px] uppercase tracking-wider text-ink-muted">Subtotal · {sharePct.toFixed(1)}%</div>
                    <div className="font-num text-lg font-bold" data-testid={`cost-subtotal-${cat.code}`}>{fmt(subtotal, currency)}</div>
                  </div>
                  {isCollapsed ? <ChevronDown className="w-4 h-4 text-ink-muted" /> : <ChevronUp className="w-4 h-4 text-ink-muted" />}
                </div>
              </button>
              {!isCollapsed && (
                <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {cat.items.map((it) => (
                    <div key={it.code} className="flex items-center gap-2">
                      <span className="text-[11px] font-num text-ink-muted w-10 shrink-0">{it.code}</span>
                      <span className="text-sm flex-1 truncate" title={it.label}>{it.label}</span>
                      <input type="text" inputMode="decimal"
                        value={valueDrafts[it.code] != null ? valueDrafts[it.code] : ""}
                        onChange={(e) => updateValue(it.code, e.target.value)}
                        data-testid={`cost-input-${it.code}`}
                        className="w-32 px-2 py-1.5 text-sm font-num border border-ink-line rounded-sm focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand text-right"
                        placeholder="0" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* TOTAL COSTS table */}
      <div className="card-flat p-6">
        <div className="stat-label">TOTAL COSTS</div>
        <table className="w-full mt-4 text-sm">
          <tbody>
            {liveBreakdown.map((c) => {
              const pct = liveFinalBid > 0 ? (c.subtotal / liveFinalBid) * 100 : 0;
              return (
                <tr key={c.code} className="border-b border-ink-line last:border-b-0">
                  <td className="py-2 font-num text-ink-muted w-10">{c.id}</td>
                  <td className="py-2">{c.label}</td>
                  <td className="py-2 text-right text-ink-muted font-num text-xs w-20">{pct.toFixed(1)}%</td>
                  <td className="py-2 text-right font-num w-40">{fmt(c.subtotal, currency)}</td>
                </tr>
              );
            })}
            <tr className="bg-secondary">
              <td colSpan={2} className="py-3 px-2 font-display font-bold tracking-tight">Final Bid Price</td>
              <td className="py-3 text-right text-ink-muted font-num text-xs">100%</td>
              <td className="py-3 text-right font-num font-bold text-lg text-brand">{fmt(liveFinalBid, currency)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Cost-composition graphs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card-flat p-6" data-testid="cost-composition-pie">
          <div className="stat-label">Cost Composition</div>
          <div className="text-[11px] text-ink-muted uppercase tracking-wider mt-1">By cost category (% of Final Bid Price)</div>
          <div className="mt-4 h-72">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={liveBreakdown.filter((c) => c.subtotal > 0).map((c) => ({ name: c.label, value: c.subtotal }))}
                  dataKey="value" cx="50%" cy="50%" outerRadius={90} label={(e) => `${(e.percent * 100).toFixed(0)}%`}>
                  {liveBreakdown.filter((c) => c.subtotal > 0).map((c, i) => <Cell key={c.code} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => fmt(v, currency)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-flat p-6" data-testid="cost-composition-bar">
          <div className="stat-label">Category Breakdown</div>
          <div className="text-[11px] text-ink-muted uppercase tracking-wider mt-1">Subtotal per category</div>
          <div className="mt-4 h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={liveBreakdown.map((c, i) => ({ name: c.label, value: c.subtotal, fill: PIE_COLORS[i % PIE_COLORS.length] }))}
                margin={{ top: 5, right: 10, left: -10, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-25} textAnchor="end" height={70} interval={0} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => fmt(v, currency)} />
                <Bar dataKey="value">
                  {liveBreakdown.map((c, i) => <Cell key={c.code} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
